<template>
  <section class="admin-calendar">
    <header class="calendar-header">
      <div class="calendar-hero">
        <h1>📅 Admin Engagement Calendar</h1>
        <p class="subtitle">
          Stay organized — track deliveries, meetings, and future commitments.
        </p>
      </div>
    </header>

    <vue-cal
      class="calendar-wrapper"
      :time-from="8 * 60"
      :time-to="20 * 60"
      default-view="month"
      hide-view-selector
      active-view="month"
      events-on-month-view="short"
      :disable-views="['years', 'year', 'week', 'day']"
      :events="events"
      locale="en"
      @cell-click="addEvent"
      @event-click="viewEvent"
    />

    <!-- Modal for viewing/editing events -->
    <transition name="fade">
      <div v-if="selectedEvent" class="modal-overlay" @click.self="closeModal">
        <div class="modal">
          <h2>{{ selectedEvent.title }}</h2>
          <p><strong>Start:</strong> {{ formatDate(selectedEvent.start) }}</p>
          <p><strong>End:</strong> {{ formatDate(selectedEvent.end) }}</p>

          <div class="modal-actions">
            <button class="delete" @click="deleteEvent(selectedEvent)">
              <i class="fa-solid fa-trash"></i> Delete
            </button>
            <button class="close" @click="closeModal">
              <i class="fa-solid fa-xmark"></i> Close
            </button>
          </div>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
import VueCal from "vue-cal";
import "vue-cal/dist/vuecal.css";

export default {
  name: "AdminCalendar",
  components: { VueCal },
  data() {
    return {
      events: [
        {
          start: "2025-11-06",
          end: "2025-11-06",
          title: "Order Delivery 🚚",
          class: "delivery-event",
        },
        {
          start: "2025-11-08",
          end: "2025-11-08",
          title: "Team Meeting 👥",
          class: "meeting-event",
        },
        {
          start: "2025-11-12",
          end: "2025-11-12",
          title: "Supplier Follow-up 📦",
          class: "followup-event",
        },
      ],
      selectedEvent: null,
    };
  },
  methods: {
    addEvent(date) {
      const title = prompt("Enter new event title:");
      if (title) {
        this.events.push({
          start: date,
          end: date,
          title,
          class: "custom-event",
        });
      }
    },
    viewEvent(event) {
      this.selectedEvent = event;
    },
    deleteEvent(event) {
      this.events = this.events.filter((e) => e !== event);
      this.selectedEvent = null;
    },
    closeModal() {
      this.selectedEvent = null;
    },
    formatDate(date) {
      return new Date(date).toLocaleString();
    },
  },
};
</script>

<style scoped>
/* --- Page Layout --- */
.admin-calendar {
  padding: 2rem;
  background: linear-gradient(135deg, #e3f2fd, #f3e5f5, #fff);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: background 0.4s ease;
}

/* --- Hero Header --- */
.calendar-header {
  text-align: center;
  width: 100%;
  background: linear-gradient(135deg, #5c6bc0, #42a5f5);
  color: white;
  padding: 2rem 1rem;
  border-radius: 16px;
  box-shadow: 0 8px 16px rgba(66, 165, 245, 0.4);
  margin-bottom: 2rem;
  position: relative;
  overflow: hidden;
}

.calendar-header::before {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.15) 10%, transparent 10.01%);
  animation: pulse 8s infinite linear;
}

.calendar-header h1 {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.3rem;
}

.subtitle {
  font-size: 1rem;
  opacity: 0.9;
}

/* --- Calendar Card --- */
.calendar-wrapper {
  width: 100%;
  max-width: 1100px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  transition: all 0.3s ease;
}

.calendar-wrapper:hover {
  transform: scale(1.01);
  box-shadow: 0 10px 30px rgba(33, 150, 243, 0.25);
}

/* --- Event Types --- */
.vuecal__event.delivery-event {
  background: linear-gradient(90deg, #4dd0e1, #26c6da);
  border-left: 4px solid #00acc1;
  color: #fff;
}

.vuecal__event.meeting-event {
  background: linear-gradient(90deg, #81c784, #66bb6a);
  border-left: 4px solid #388e3c;
  color: #fff;
}

.vuecal__event.followup-event {
  background: linear-gradient(90deg, #ffb74d, #ff9800);
  border-left: 4px solid #f57c00;
  color: #fff;
}

.vuecal__event.custom-event {
  background: linear-gradient(90deg, #ba68c8, #ab47bc);
  border-left: 4px solid #8e24aa;
  color: #fff;
}

/* Hover Glow Effect */
.vuecal__event:hover {
  transform: scale(1.06);
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.6);
  transition: all 0.2s ease;
}

/* --- Modal Styles --- */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(20, 20, 20, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
}

.modal {
  background: linear-gradient(135deg, #ffffff, #f3f4f6);
  color: #222;
  border-radius: 14px;
  padding: 1.5rem;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  width: 90%;
  max-width: 400px;
  animation: fadeIn 0.3s ease-in-out;
}

.modal h2 {
  margin-bottom: 1rem;
  color: #1976d2;
}

.modal-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 1.2rem;
}

.modal-actions button {
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
}

.modal-actions .delete {
  background: #ef5350;
  color: white;
}

.modal-actions .delete:hover {
  background: #c62828;
}

.modal-actions .close {
  background: #42a5f5;
  color: white;
}

.modal-actions .close:hover {
  background: #1e88e5;
}

/* --- Animations --- */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.92);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes pulse {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* --- Transitions --- */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* --- Responsive --- */
@media (max-width: 900px) {
  .calendar-wrapper {
    padding: 0.6rem;
  }
  .calendar-header h1 {
    font-size: 1.6rem;
  }
}

@media (max-width: 700px) {
  .calendar-header {
    padding: 1.5rem 1rem;
  }
  .subtitle {
    font-size: 0.85rem;
  }
  .modal {
    width: 95%;
    padding: 1rem;
  }
}
</style>
